// The chart's vector glyphs (R548).
//
// The map pieces and the KEYS legend must draw the SAME mark. The map draws
// into the engine's instanced batches; the legend draws into a small DOM
// canvas. One owner therefore has to speak both, so every glyph here is a
// pure function that emits into the sink interfaces of
// `fast/layers/batches.ts` — and `render/canvas-sink.ts` implements those
// three interfaces over a Canvas2D context. A legend row and its map piece
// then share one test table of call counts, and a glyph cannot drift
// between the two places it appears.
//
// The sinks are imported as TYPES ONLY. A type import is erased at build
// time, so this leaf still carries no path into the engine: the interfaces
// are structural, and the recorder of `testkit/recording.ts` satisfies
// them exactly as a real batch does.
//
// **THE FIGURES ARE THE MOCKUP'S.** Every glyph is ported line by line from
// the frozen cards — `archive/mockups/029-chart-furniture-2.html` (star,
// cross, anchor, shield, fish, ribbon) and `031-chart-furniture-3.html`
// (cone, tower, swords, crane, pennant) — at the size the card drew it. A
// piece scales the glyph; it does not restate the shape.
//
// **A CURVE IS A POLYLINE.** The segment sink draws capsules, so every
// quadratic of a card becomes CURVE_STEPS straight pieces. Six to eight is
// where the eye stops seeing the corners at the sizes these marks are drawn.
//
// **THE SINKS HAVE NO POLYGON FILL.** The quad sink lays axis-aligned
// rectangles and atlas sprites, and nothing here is either. A glyph the card
// filled — the pennant, the flare, the tower's plinth — is therefore drawn
// as its outline, with the one exception noted at `tower`.
//
// **A GLYPH ALLOCATES NOTHING.** A piece calls a glyph for every mark it
// shows, every frame (§10.7), so a glyph writes each segment straight into
// the sink: no point list, no closure, no per-call table. The tables a glyph
// does read stand at module scope.

import type { CircleSink, SegmentSink } from '../fast/layers/batches';
import { bezier2 } from './recipe-kit';

/** How many straight pieces one quadratic of a card becomes. */
const CURVE_STEPS = 7;

/** A point in the glyph's own frame, before the piece places it. */
type P = readonly [number, number];

/** The two sides of a mirrored part. */
const SIDES = [-1, 1] as const;

/** The four corners about a point, as sign pairs. */
const CORNERS = [[-1, -1], [1, -1], [-1, 1], [1, 1]] as const;

/**
 * A quadratic Bézier from `(x0, y0)` to `(x1, y1)`, drawn as `CURVE_STEPS`
 * straight pieces from the start point on.
 */
function curveTo(
  seg: SegmentSink, x0: number, y0: number, cx: number, cy: number,
  x1: number, y1: number, color: number, alpha: number, width: number,
): void {
  let px = x0, py = y0;
  for (let s = 1; s <= CURVE_STEPS; s++) {
    const t = s / CURVE_STEPS;
    const x = bezier2(x0, cx, x1, t), y = bezier2(y0, cy, y1, t);
    seg.line(px, py, x, y, width, color, alpha);
    px = x; py = y;
  }
}

/**
 * The same curve as `CURVE_STEPS` points, the start point left out. It runs
 * at module load alone, for a shape a glyph places as a whole (`wreck`).
 */
function curve(p0: P, c: P, p1: P): P[] {
  const out: P[] = [];
  for (let s = 1; s <= CURVE_STEPS; s++) {
    const t = s / CURVE_STEPS;
    out.push([bezier2(p0[0], c[0], p1[0], t), bezier2(p0[1], c[1], p1[1], t)]);
  }
  return out;
}

/**
 * A run of glyph-unit points, turned by `(cos, sin)`, lifted by `dy`, scaled
 * by `s` and placed at `(x, y)`: the card's `at()` of `wreck`, applied point
 * by point. `close` joins the last point back to the first.
 */
function polyAt(
  seg: SegmentSink, pts: readonly P[], x: number, y: number,
  cos: number, sin: number, dy: number, s: number,
  color: number, alpha: number, width: number, close: boolean,
): void {
  let x0 = 0, y0 = 0, px = 0, py = 0;
  for (let i = 0; i < pts.length; i++) {
    const gx = pts[i][0], gy = pts[i][1];
    const qx = x + (gx * cos - gy * sin) * s;
    const qy = y + (dy + gx * sin + gy * cos) * s;
    if (i === 0) { x0 = qx; y0 = qy; } else seg.line(px, py, qx, qy, width, color, alpha);
    px = qx; py = qy;
  }
  if (close && pts.length > 2) seg.line(px, py, x0, y0, width, color, alpha);
}

/** A straight line of glyph units, `x0, y0, x1, y1`. */
type GlyphLine = readonly [number, number, number, number];

/** Each line of `lines`, scaled by `s` and placed at `(x, y)`. */
function linesAt(
  seg: SegmentSink, lines: readonly GlyphLine[], x: number, y: number, s: number,
  color: number, alpha: number, width: number,
): void {
  for (let i = 0; i < lines.length; i++) {
    const m = lines[i];
    seg.line(x + m[0] * s, y + m[1] * s, x + m[2] * s, y + m[3] * s, width, color, alpha);
  }
}

/**
 * A star of three crossed strokes — the chart's mark for a rock awash
 * (card 26, modern). `r` is the arm's half length.
 */
export function star(
  seg: SegmentSink, x: number, y: number, r: number,
  color: number, alpha: number, width = 0.9,
): void {
  for (let k = 0; k < 3; k++) {
    const t = (k * Math.PI) / 3;
    const dx = Math.cos(t) * r, dy = Math.sin(t) * r;
    seg.line(x - dx, y - dy, x + dx, y + dy, width, color, alpha);
  }
}

/**
 * A cross, and on ask the four dots that make it the chart's danger mark
 * (card 26). `r` is the arm's half length; the card drew the dots at 3 px
 * for an arm of 3.5, so the offset follows `r` and the dot stays 0.7 px.
 */
export function crossDots(
  seg: SegmentSink, circ: CircleSink, x: number, y: number, r: number,
  color: number, alpha: number, width = 0.9, dots = false,
): void {
  seg.line(x - r, y, x + r, y, width, color, alpha);
  seg.line(x, y - r, x, y + r, width, color, alpha);
  if (!dots) return;
  const off = (r * 6) / 7;
  for (let k = 0; k < CORNERS.length; k++) {
    circ.disc(x + CORNERS[k][0] * off, y + CORNERS[k][1] * off, 0.7, color, alpha);
  }
}

/**
 * Two crossed swords — the historical mark for a battle (card 52). The pair
 * stands at 45 degrees either side of the vertical, as the card drew them.
 */
export function swords(
  seg: SegmentSink, circ: CircleSink, x: number, y: number,
  color: number, alpha: number, width = 1.1,
): void {
  for (let i = 0; i < SIDES.length; i++) {
    const a = (SIDES[i] * Math.PI) / 4;
    const cos = Math.cos(a), sin = Math.sin(a);
    // The card's `at(px, py)` is `(x + px·cos − py·sin, y + px·sin + py·cos)`,
    // written out for the blade (0, −9)→(0, 7), the guard (−3, 4)→(3, 4) and
    // the pommel (0, 8).
    seg.line(x + 9 * sin, y - 9 * cos, x - 7 * sin, y + 7 * cos, width, color, alpha);
    seg.line(
      x - 3 * cos - 4 * sin, y - 3 * sin + 4 * cos,
      x + 3 * cos - 4 * sin, y + 3 * sin + 4 * cos, width, color, alpha,
    );
    circ.disc(x - 8 * sin, y + 8 * cos, 1, color, alpha);
  }
}

/**
 * The lettered diamond of a tidal station (card 15). `hw` and `hh` are the
 * half width and the half height; the card drew 5.5 by 7.
 */
export function diamond(
  seg: SegmentSink, x: number, y: number, hw: number, hh: number,
  color: number, alpha: number, width = 0.9,
): void {
  const t = y - hh, b = y + hh, l = x - hw, r = x + hw;
  seg.line(x, t, r, y, width, color, alpha);
  seg.line(r, y, x, b, width, color, alpha);
  seg.line(x, b, l, y, width, color, alpha);
  seg.line(l, y, x, t, width, color, alpha);
}

/** The crane of works in progress (card 58): a mast, a jib, a fall, a base. */
export function crane(
  seg: SegmentSink, x: number, y: number,
  color: number, alpha: number, width = 0.9,
): void {
  seg.line(x, y, x, y - 14, width, color, alpha);
  seg.line(x, y - 14, x + 10, y - 14, width, color, alpha);
  seg.line(x + 10, y - 14, x + 10, y - 9, width, color, alpha);
  seg.line(x - 4, y, x + 4, y, width, color, alpha);
}

/**
 * The anchor of a port (card 23, modern). `scale` is the card's `sc`; the
 * stock is the pair of short strokes at the crown, which the historical
 * reading wears and the modern one does not.
 */
export function anchorGlyph(
  seg: SegmentSink, circ: CircleSink, x: number, y: number,
  color: number, alpha: number, scale = 1, stock = false,
): void {
  // The shank (0, −7)→(0, 6), the stock (−4, −4)→(4, −4) and the ring at
  // (0, −8.5), each figure times `scale` about the point.
  seg.line(x, y - 7 * scale, x, y + 6 * scale, 1, color, alpha);
  seg.line(x - 4 * scale, y - 4 * scale, x + 4 * scale, y - 4 * scale, 1, color, alpha);
  circ.ring(x, y - 8.5 * scale, 1.5 * scale, 0.9, color, alpha);
  // The arms: the card's arc from 0.2 to pi − 0.2 about a point below.
  circ.arc(x, y + scale, 5 * scale, 1, 0.2, Math.PI - 0.4, color, alpha);
  if (!stock) return;
  for (let i = 0; i < SIDES.length; i++) {
    const s = SIDES[i];
    seg.line(x + s * 3 * scale, y + 5 * scale, x + s * scale, y + 3 * scale, 0.8, color, alpha);
  }
}

/** The fishing-ground fish of a bank (card 27, modern). */
export function fish(
  seg: SegmentSink, x: number, y: number,
  color: number, alpha: number, width = 0.8,
): void {
  // The body: the nose at (−6, 0), the tail root at (5, 0), one curve each
  // way between them; then the tail, closed on the root.
  const nose = x - 6, root = x + 5;
  curveTo(seg, nose, y, x, y - 4, root, y, color, alpha, width);
  curveTo(seg, root, y, x, y + 4, nose, y, color, alpha, width);
  seg.line(root, y, x + 8, y - 3, width, color, alpha);
  seg.line(x + 8, y - 3, x + 8, y + 3, width, color, alpha);
  seg.line(x + 8, y + 3, root, y, width, color, alpha);
}

/**
 * The magenta flare that marks a light (card 17, modern): a teardrop that
 * stands `h` px tall, the card's 13 px form scaled to it.
 */
export function flare(
  seg: SegmentSink, x: number, y: number, h: number,
  color: number, alpha: number, width = 0.9,
): void {
  const k = h / 13;
  // Up the left side from the foot to the tip, and down the right.
  const tip = y - 13 * k, waist = y - 9 * k;
  curveTo(seg, x, y, x - 7 * k, waist, x, tip, color, alpha, width);
  curveTo(seg, x, tip, x + 7 * k, waist, x, y, color, alpha, width);
}

/**
 * The historical form of the same light: seven rays from the tower (card
 * 17). `r0` is where a ray begins and `r1` where it ends.
 */
export function flareRays(
  seg: SegmentSink, x: number, y: number,
  color: number, alpha: number, width = 0.7, r0 = 4, r1 = 11,
): void {
  for (let k = 0; k < 7; k++) {
    const a = -Math.PI + (k * Math.PI) / 6;
    const cos = Math.cos(a), sin = Math.sin(a);
    seg.line(x + cos * r0, y + sin * r0, x + cos * r1, y + sin * r1, width, color, alpha);
  }
}

/** A small triangular cone, point up or point down (card 49). */
export function cone(
  seg: SegmentSink, x: number, y: number, up: boolean,
  color: number, alpha: number, width = 0.9,
): void {
  const base = y + (up ? 3 : -3), apex = y + (up ? -4 : 4);
  seg.line(x - 4, base, x + 4, base, width, color, alpha);
  seg.line(x + 4, base, x, apex, width, color, alpha);
  seg.line(x, apex, x - 4, base, width, color, alpha);
}

/** The cosine and the sine of the six plinth corners of `tower`, at -30°,
 *  30° … 270°, worked out once from the angles the loop used to work out
 *  each call (§10). */
const TOWER_COS: readonly number[] = [0, 1, 2, 3, 4, 5].map((k) => Math.cos(((60 * k - 30) * Math.PI) / 180));
const TOWER_SIN: readonly number[] = [0, 1, 2, 3, 4, 5].map((k) => Math.sin(((60 * k - 30) * Math.PI) / 180));

/**
 * A settled Sentinel (card 49): a hex plinth, a mast and a yard.
 *
 * The card fills the plinth faintly. The sinks have no polygon fill, so the
 * fill is a disc of the hex's inradius under the outline — the one place a
 * glyph here answers a fill with another shape, and it is invisible at the
 * size the mark is drawn.
 */
export function tower(
  seg: SegmentSink, circ: CircleSink, x: number, y: number,
  color: number, alpha: number, width = 1, r = 7,
): void {
  const cy = y + 3;
  circ.disc(x, cy, r * 0.866, color, alpha * 0.12);
  // The six sides of the plinth, the last one back to the first corner.
  let x0 = 0, y0 = 0, px = 0, py = 0;
  for (let k = 0; k < 6; k++) {
    const qx = x + TOWER_COS[k] * r, qy = cy + TOWER_SIN[k] * r;
    if (k === 0) { x0 = qx; y0 = qy; } else seg.line(px, py, qx, qy, width, color, alpha);
    px = qx; py = qy;
  }
  seg.line(px, py, x0, y0, width, color, alpha);
  seg.line(x, y - 3, x, y - 12, 1.2, color, alpha);
  seg.line(x - 3, y - 12, x + 3, y - 12, 1.2, color, alpha);
}

/**
 * The shield of a faction seat (card 23, historical). `w` and `h` are the
 * full width and height; the card drew 15 by 19.
 */
export function shieldOutline(
  seg: SegmentSink, x: number, y: number, w: number, h: number,
  color: number, alpha: number, width = 1,
): void {
  const l = x - w / 2, r = x + w / 2, t = y - h / 2, b = y + h / 2;
  // The chief, the right flank, the two curves of the base, the left flank.
  const flank = y + h * 0.1, bend = y + h * 0.4;
  seg.line(l, t, r, t, width, color, alpha);
  seg.line(r, t, r, flank, width, color, alpha);
  curveTo(seg, r, flank, r, bend, x, b, color, alpha, width);
  curveTo(seg, x, b, l, bend, l, flank, color, alpha, width);
  seg.line(l, flank, l, t, width, color, alpha);
}

/**
 * A clan burgee on its staff (card 56). The hoist hangs from `(x, y − h)` to
 * `(x, y)` and the fly reaches `w` to the right.
 *
 * `notch` cuts the swallow tail: 0 is the plain pennant the card drew, and a
 * fraction of the fly is how deep the notch bites into it.
 */
export function swallowTail(
  seg: SegmentSink, x: number, y: number, w: number, h: number,
  color: number, alpha: number, width = 1, notch = 0,
): void {
  seg.line(x, y + h * 0.66, x, y - h, width, color, alpha);
  const top = y - h, fly = x + w, mid = y - h / 2;
  if (notch > 0) {
    const bite = x + w * (1 - notch);
    seg.line(x, top, fly, y - h * 0.8, width, color, alpha);
    seg.line(fly, y - h * 0.8, bite, mid, width, color, alpha);
    seg.line(bite, mid, fly, y - h * 0.2, width, color, alpha);
    seg.line(fly, y - h * 0.2, x, y, width, color, alpha);
  } else {
    seg.line(x, top, fly, mid, width, color, alpha);
    seg.line(fly, mid, x, y, width, color, alpha);
  }
  seg.line(x, y, x, top, width, color, alpha);
}

/**
 * The scroll a region name rides on (card 35, historical): a panel with
 * rolled ends and a tail behind each end.
 */
export function scrollRibbon(
  seg: SegmentSink, x: number, y: number, w: number, h: number,
  color: number, alpha: number, width = 0.9,
): void {
  const l = x - w / 2, r = x + w / 2, t = y - h / 2, b = y + h / 2;
  // The panel: along the top, round the right roll, back along the bottom,
  // round the left roll.
  seg.line(l + 8, t, r - 8, t, width, color, alpha);
  curveTo(seg, r - 8, t, r + 4, t + 2, r - 2, y, color, alpha, width);
  curveTo(seg, r - 2, y, r + 4, b - 2, r - 8, b, color, alpha, width);
  seg.line(r - 8, b, l + 8, b, width, color, alpha);
  curveTo(seg, l + 8, b, l - 4, b - 2, l + 2, y, color, alpha, width);
  curveTo(seg, l + 2, y, l - 4, t + 2, l + 8, t, color, alpha, width);
  // The tail behind each roll: five points, closed.
  for (let i = 0; i < SIDES.length; i++) {
    const s = SIDES[i], ex = s < 0 ? l + 2 : r - 2;
    const far = ex + s * 14, near = ex + s * 9;
    seg.line(ex, y - 4, far, y - 9, width, color, alpha);
    seg.line(far, y - 9, near, y + 1, width, color, alpha);
    seg.line(near, y + 1, far, y + 11, width, color, alpha);
    seg.line(far, y + 11, ex, y + 6, width, color, alpha);
    seg.line(ex, y + 6, ex, y - 4, width, color, alpha);
  }
}

// ---------------------------------------------------------------------------
// The five faction charges of card 23
// ---------------------------------------------------------------------------
//
// `data/identity/identity.toml` gives every faction a `charge`, and
// `gen/balance.ts` carries the word as `FACTIONS[i].charge`. The card drew
// two of the five — the waves and the bezants — on a shield 15 px wide. The
// other three are drawn in the same manner and at the same scale. A caller
// states the shield's WIDTH and every figure follows it, so one charge fits
// every shield the pack asks for.

/** The three roundels of the bezants: one in chief, two in base. */
const BEZANTS = [[0, -5], [-4, 2], [4, 2]] as const;

/**
 * One faction's charge, centred on `(x, y)` and scaled to a shield `w` wide.
 *
 * An unknown word draws nothing rather than a placeholder: a pack that
 * invents a sixth charge must land its drawing here, and a blank shield
 * says so louder than a wrong mark does.
 */
export function charge(
  name: string, seg: SegmentSink, circ: CircleSink, x: number, y: number,
  w: number, color: number, alpha: number, width = 0.9,
): void {
  const k = w / 15;
  switch (name) {
    case 'waves': {
      // The card's own three barrulets wavy: one sine over twelve px.
      for (let row = 0; row < 3; row++) {
        let px = 0, py = 0;
        for (let s = 0; s <= 12; s += 2) {
          const wy = -5 + row * 5 + Math.sin((s / 12) * Math.PI * 2) * 1.2;
          const qx = x + (-6 + s) * k, qy = y + wy * k;
          if (s > 0) seg.line(px, py, qx, qy, width, color, alpha);
          px = qx; py = qy;
        }
      }
      return;
    }
    case 'bezants': {
      // Three roundels: one in chief, two in base.
      for (let i = 0; i < BEZANTS.length; i++) {
        circ.disc(x + BEZANTS[i][0] * k, y + BEZANTS[i][1] * k, 2 * k, color, alpha);
      }
      return;
    }
    case 'gears': {
      circ.ring(x, y, 5 * k, width, color, alpha);
      for (let i = 0; i < 6; i++) {
        const a = (i * Math.PI) / 3;
        const cos = Math.cos(a), sin = Math.sin(a);
        seg.line(
          x + cos * 5 * k, y + sin * 5 * k,
          x + cos * 7.5 * k, y + sin * 7.5 * k, width, color, alpha,
        );
      }
      return;
    }
    case 'frond': {
      // The stem runs from 7 below the centre to 3.5 above it, and the top
      // leaf tip stands 6 above it: the figure spans -6 to 7, 1 px lower
      // than the other charges.
      seg.line(x, y + 7 * k, x, y - 3.5 * k, width, color, alpha);
      for (let i = 0; i < 3; i++) {
        const ly = (4.5 - i * 4) * k;
        for (let j = 0; j < SIDES.length; j++) {
          seg.line(x, y + ly, x + SIDES[j] * 4 * k, y + ly - 2.5 * k, width, color, alpha);
        }
      }
      return;
    }
    case 'chevronels': {
      for (let i = 0; i < 3; i++) {
        const cy = (-6 + i * 5) * k;
        seg.line(x - 5 * k, y + cy + 2.5 * k, x, y + cy - 1.5 * k, width, color, alpha);
        seg.line(x, y + cy - 1.5 * k, x + 5 * k, y + cy + 2.5 * k, width, color, alpha);
      }
      return;
    }
    default:
      return;
  }
}

/** The INT 1 wreck in glyph units: the hull arc under its base line, and the masts. */
const WRECK_BOWL: readonly P[] = [[-8, 0], ...curve([-8, 0], [0, 5], [8, 0])];
const WRECK_MASTS: readonly GlyphLine[] = [
  [-8, 0, 8, 0], [-5, 0, -5, -5], [0, 0, 0, -6], [5, 0, 5, -5],
];
/** The card's ship on her side, in glyph units: the closed bowl and one mast. */
const WRECK_HULL: readonly P[] = [[-9, 0], ...curve([-9, 0], [0, 6], [9, 0]), [6, -2], [-6, -2]];
const WRECK_MAST: readonly P[] = [[0, -2], [3, -11]];
const WRECK_COS = Math.cos(-0.5);
const WRECK_SIN = Math.sin(-0.5);

/**
 * A wreck on the sea bed (card 08, R801).
 *
 * The historical reading draws the card's ship on her side: the hull as a
 * closed bowl, turned half a radian, and one mast. The modern reading draws
 * the INT 1 wreck symbol: the hull arc under a base line, and three masts.
 * The card's dashed hexagon is the ruin of its swatch and not part of the
 * symbol, so no hexagon is drawn. `s` scales the card's figures.
 */
export function wreck(
  seg: SegmentSink, x: number, y: number, modern: boolean,
  color: number, alpha: number, width = 0.9, s = 1,
): void {
  if (modern) {
    polyAt(seg, WRECK_BOWL, x, y, 1, 0, 0, s, color, alpha, width, false);
    linesAt(seg, WRECK_MASTS, x, y, s, color, alpha, width);
    return;
  }
  // The card turns the hull by -0.5 rad about a point 2 px over the site.
  polyAt(seg, WRECK_HULL, x, y, WRECK_COS, WRECK_SIN, -2, s, color, alpha, width, true);
  polyAt(seg, WRECK_MAST, x, y, WRECK_COS, WRECK_SIN, -2, s, color, alpha, width, false);
}

/**
 * The cargo bale of round 132 card 01 (R2847), in glyph units. The modern
 * bale is a square on the line of the sea bed, right of the keel: its
 * left side, its top, its side and the cords of `WRECK_BALE_CORDS`. The
 * square's fill is a quad the piece lays, because the glyph writes lines
 * alone; `WRECK_BALE_BOX` states where it stands.
 */
export const WRECK_BALE_BOX = { x0: 10, y0: -5, side: 5 } as const;
const WRECK_BALE_SQUARE: readonly P[] = [[10, -5], [15, -5], [15, 0], [10, 0]];
const WRECK_BALE_CORDS: readonly GlyphLine[] = [
  [12.5, -5, 12.5, 0], [10, -2.5, 15, -2.5],
];

/**
 * The historical bale, in glyph units with no turn: an engraved bale of
 * two curved ends and two cords, turned 0.18 rad about (11, 2.5), flat on
 * the bed below the stern of the ship on her side. The card's frame is
 * applied here, at module load, so the glyph places the points as a whole.
 */
const BALE_TURN = 0.18;
const BALE_AT: P = [11, 2.5];
function baleAt(pts: readonly P[]): P[] {
  const c = Math.cos(BALE_TURN), s = Math.sin(BALE_TURN);
  return pts.map(([x, y]) => [BALE_AT[0] + x * c - y * s, BALE_AT[1] + x * s + y * c] as P);
}
const WRECK_BALE_HIST: readonly P[] = baleAt([
  [-3.2, -2.2], ...curve([-3.2, -2.2], [0, -3], [3.2, -2.2]),
  [3.2, 2.2], ...curve([3.2, 2.2], [0, 3], [-3.2, 2.2]),
]);
const WRECK_BALE_HIST_CORDS: readonly (readonly P[])[] = [
  baleAt([[-1.1, -2.6], [-1.1, 2.6]]), baleAt([[1.1, -2.6], [1.1, 2.6]]),
];

/**
 * The cargo bale of a wreck the viewer holds a cargo sighting of (round
 * 132 card 01, R2847). It draws beside the `wreck` glyph at the same
 * place, ink, width and scale; the hull symbol stays whole. The modern
 * reading draws the square and its two cords; the historical one the
 * engraved bale, flat on the bed below the stern.
 */
export function wreckBale(
  seg: SegmentSink, x: number, y: number, modern: boolean,
  color: number, alpha: number, width = 0.9, s = 1,
): void {
  if (modern) {
    polyAt(seg, WRECK_BALE_SQUARE, x, y, 1, 0, 0, s, color, alpha, width, true);
    linesAt(seg, WRECK_BALE_CORDS, x, y, s, color, alpha, width);
    return;
  }
  polyAt(seg, WRECK_BALE_HIST, x, y, 1, 0, 0, s, color, alpha, width, true);
  for (let i = 0; i < WRECK_BALE_HIST_CORDS.length; i++) {
    polyAt(seg, WRECK_BALE_HIST_CORDS[i], x, y, 1, 0, 0, s, color, alpha, width, false);
  }
}

/** A box in glyph units round the place of a glyph. */
export interface GlyphBox { x0: number; y0: number; x1: number; y1: number }

/** Grows `b` by points placed as `polyAt` places them at (0, 0), scale 1. */
function growGlyphBox(b: GlyphBox, pts: readonly P[], cos: number, sin: number, dy: number): void {
  for (const [gx, gy] of pts) {
    const qx = gx * cos - gy * sin, qy = dy + gx * sin + gy * cos;
    b.x0 = Math.min(b.x0, qx); b.y0 = Math.min(b.y0, qy);
    b.x1 = Math.max(b.x1, qx); b.y1 = Math.max(b.y1, qy);
  }
}

/** The ends of a list of lines, as points. */
function lineEnds(lines: readonly GlyphLine[]): P[] {
  return lines.flatMap((m) => [[m[0], m[1]], [m[2], m[3]]] as P[]);
}

function wreckBoxOf(modern: boolean, cargo: boolean): GlyphBox {
  const b: GlyphBox = { x0: Infinity, y0: Infinity, x1: -Infinity, y1: -Infinity };
  if (modern) {
    growGlyphBox(b, WRECK_BOWL, 1, 0, 0);
    growGlyphBox(b, lineEnds(WRECK_MASTS), 1, 0, 0);
    if (cargo) growGlyphBox(b, [...WRECK_BALE_SQUARE, ...lineEnds(WRECK_BALE_CORDS)], 1, 0, 0);
    return b;
  }
  growGlyphBox(b, WRECK_HULL, WRECK_COS, WRECK_SIN, -2);
  growGlyphBox(b, WRECK_MAST, WRECK_COS, WRECK_SIN, -2);
  if (cargo) growGlyphBox(b, [...WRECK_BALE_HIST, ...WRECK_BALE_HIST_CORDS.flat()], 1, 0, 0);
  return b;
}

/**
 * The drawn box of a wreck in glyph units, for each reading, plain and
 * with the cargo bale (R2851): the hit area of the press and the hover of
 * a wreck mark. The piece scales it by `fig.scale`. It reads the points
 * that `wreck` and `wreckBale` draw, so a new outline moves the box too.
 */
export const WRECK_BOXES: Readonly<Record<'modern' | 'historical', { plain: GlyphBox; cargo: GlyphBox }>> = {
  modern: { plain: wreckBoxOf(true, false), cargo: wreckBoxOf(true, true) },
  historical: { plain: wreckBoxOf(false, false), cargo: wreckBoxOf(false, true) },
};
